package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.ImageCreditStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

// --- Constants ---
private val HERO_IMAGE_MAX_WIDTH = 600.px
private val FIGURE_IMAGE_MAX_WIDTH = 600.px

@InitRoute
fun initTypesOfMetamorphic(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

private val metamorphicKeyCharacteristics = listOf(
    "Foliation" to "Platy minerals line up at right angles to the squeeze, giving the rock a grain, a sheen, or bands of light and dark. It is the single most reliable sign that a rock has been metamorphosed."
    , "Recrystallized grains" to "Old grains regrow into a tight, interlocking mosaic, which usually leaves the rock harder, denser, and less porous than what it started as."
    , "Distorted structures" to "Original bedding and fossils get stretched, folded, or wiped out entirely. A fossil that survives is generally smeared out of shape."
    , "New minerals" to "Garnet, staurolite, kyanite, and mica crystals grow in as the rock adjusts to new conditions, and which ones appear tells a geologist how hot and deep it went."
    , "Hardness" to "Usually 6 or above for foliated and quartz-rich types. Marble is the exception at about 3, soft enough to scratch with a knife."
)

/** What drove the change, and whether the rock came out with a grain. */
private val metamorphicFamilies = listOf(
    "Regional metamorphism" to "Whole belts of crust cooked and squeezed during mountain building. Produces the foliated rocks — slate, phyllite, schist, and gneiss — in that order as conditions intensify."
    , "Contact metamorphism" to "Rock baked by a nearby body of magma, with heat but little directed pressure. Produces non-foliated rocks such as marble, quartzite, and hornfels."
    , "Foliated vs. non-foliated" to "Foliated rocks split or band because platy minerals aligned; non-foliated rocks stay massive, usually because they are built from a single mineral with nothing to line up."
)

/** Named metamorphic rocks, paired with what they started as and what sets them apart. */
private val metamorphicExamples = listOf(
    "Slate" to "Shale under low-grade metamorphism. Splits into flat, hard sheets that ring when tapped — the reason it ended up on roofs and pool tables."
    , "Phyllite" to "One step past slate. The mica grains have grown enough to give the surface a distinct silky sheen."
    , "Schist" to "Coarse, visibly flaky mica with obvious layering, and the most likely place to find garnets sitting in plain view."
    , "Gneiss" to "High-grade rock with light and dark minerals separated into bold bands. Often started as granite or as a deeply buried sedimentary pile."
    , "Marble" to "Recrystallized limestone. Sugary texture, still fizzes in acid, and soft enough to carve, which is why sculptors have used it for millennia."
    , "Quartzite" to "Sandstone whose grains have fused into solid quartz. Very hard, breaks straight through the grains instead of around them, and takes a good polish."
    , "Hornfels" to "Fine-grained, tough, and dark, baked in the aureole around an intrusion. Gives a flinty ring when struck."
    , "Serpentinite" to "Green, slippery-feeling rock formed where seawater alters mantle peridotite. Often mottled and waxy; the ornamental variety is sold as verd antique."
    , "Soapstone (Steatite)" to "Talc-rich, greasy to the touch, and soft enough to shape with hand tools. Long used for bowls, pipes, and carvings."
    , "Anthracite" to "Coal taken far enough to count as metamorphic. Hard, glassy black, and the cleanest-burning grade."
)

@Composable
private fun MetamorphicFigure(src: String, alt: String, caption: String, credit: String) {
    Column(
        Modifier
            .fillMaxWidth()
            .gap(0.5.cssRem)
        , horizontalAlignment = CenterHorizontally
    ) {
        Image(
            src = src
            , alt = alt
            , modifier = Modifier
                .maxWidth(FIGURE_IMAGE_MAX_WIDTH)
                .fillMaxWidth()  // shrinks naturally on small screens
        )
        P { Em { Text(caption) } }
        SpanText(credit, ImageCreditStyle.toModifier())
    }
}

@Composable
private fun MetamorphicSectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun TypesOfMetamorphicHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Metamorphic Rocks"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TypesOfMetamorphicHero() {

    TypesOfMetamorphicHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/metamorphic/lewisian_gneiss_rhiconich.jpg"
                    , alt = "Banded Lewisian gneiss cliff at Rhiconich, Scotland"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
                P { Em { Text("Lewisian gneiss at Rhiconich, Scotland — some of the oldest rock in Europe, banded and cut by later veins.") } }
                SpanText(
                    "Photo: NachoBen, CC BY-SA 4.0, via Wikimedia Commons"
                    , ImageCreditStyle.toModifier()
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Metamorphic rock is rock that has been changed without ever melting. Heat, pressure, and hot chemically active fluids rearrange the minerals of an existing igneous, sedimentary, or metamorphic rock while it stays solid, and what comes out the other side can look nothing like what went in. Every metamorphic rock has a parent, and identifying it is half the fun.")
            }

            MetamorphicSectionTitle("How They Form")
            P {
                Text("Burial and mountain building drive rock to depths where temperatures run from roughly 200°C up to near its melting point, with pressure from the weight of everything above it and from plates pushing sideways. Minerals that are no longer stable break down and regrow as ones that are, and if the pressure pushes harder from one direction than another, the new crystals grow lined up — which is what produces foliation. Push much further and the rock melts, at which point it is igneous again and the cycle starts over.")
            }
        }
    }

    Column(
        HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Ul(PageListStyle.toAttrs()) {
            metamorphicFamilies.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        MetamorphicFigure(
            src = "/images/typesof/metamorphic/isoclinal_fold_gneiss_montana.jpg"
            , alt = "Tightly folded gneiss in a Montana roadcut"
            , caption = "An isoclinal fold in Archean gneiss, Madison County, Montana — light and dark bands folded double while the rock stayed solid."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        MetamorphicSectionTitle("Key Characteristics")

        Ul(PageListStyle.toAttrs()) {
            metamorphicKeyCharacteristics.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        MetamorphicFigure(
            src = "/images/typesof/metamorphic/gneiss_himalaya.jpg"
            , alt = "Gneiss sample showing alternating light and dark bands"
            , caption = "Gneiss from the Indian Himalayas — pale quartz and feldspar separated into bands by dark mica, the textbook look of high-grade foliation."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )

        MetamorphicSectionTitle("Common Examples")

        Ul(PageListStyle.toAttrs()) {
            metamorphicExamples.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        MetamorphicFigure(
            src = "/images/typesof/metamorphic/slate_minnesota.jpg"
            , alt = "Slate outcrop splitting along flat cleavage planes"
            , caption = "Slate in a roadcut near Gilbert, Minnesota — low-grade metamorphosed shale that splits along flat cleavage planes rather than along its original bedding."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )

        MetamorphicFigure(
            src = "/images/typesof/metamorphic/garnet_schist.jpg"
            , alt = "Garnet schist with dark red garnet crystals"
            , caption = "Garnet schist — deep red garnets grown in place in a silvery, foliated matrix as the rock reached schist grade."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )

        MetamorphicFigure(
            src = "/images/typesof/metamorphic/marble_carrara.jpg"
            , alt = "Polished white Carrara marble with grey veining"
            , caption = "Statuario marble from Carrara, Italy — limestone recrystallized into the stone sculptors have quarried for two thousand years."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )

        MetamorphicFigure(
            src = "/images/typesof/metamorphic/quartzite_beartooth_montana.jpg"
            , alt = "Quartzite sample from the Beartooth Mountains"
            , caption = "Quartzite from the Beartooth Mountains, Montana — sandstone whose grains have fused so completely that breaks run straight through them."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )

        MetamorphicSectionTitle("For Rockhounds")
        P {
            Text("Metamorphic ground is where the display crystals come from. Schist and gneiss carry garnet, staurolite, and kyanite, serpentinite belts turn up jade and rhodonite, and marble contacts are worth checking for well-formed crystals grown in the baked zone. Old quarries and roadcuts give the cleanest look at foliation, and because these rocks are tough, the good pieces survive a long trip downstream — so check the cobbles in the creek below an outcrop, not just the outcrop itself.")
        }
    }
}

@Page("/types_of_rocks/metamorphic")
@Layout(".components.layouts.PageLayout")
@Composable
fun TypesOfMetamorphicPage() {
    TypesOfMetamorphicHero()
}
